import { useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { syncUserDetails } from 'actions/AuthActions'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import refreshLoyaltyAccountAfterTierClaim from 'actions/loyalty/refreshLoyaltyAccountAfterTierClaim'
import fetchLoyaltyClaimCampaigns from 'actions/loyalty/fetchLoyaltyClaimCampaigns'
import { me } from 'api/auth'
import fetchLoyaltyAccount from 'actions/loyalty/fetchLoyaltyAccount'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useTierUpgradeCampaigns from 'loyalty/hooks/useTierUpgradeCampaigns'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { claimTierUpgradeCampaign } from 'loyalty/tierUpgradeCampaign/claimTierUpgradeCampaign'
import {
  canAutoClaimCampaign,
  getCampaignTierStanding,
  isAlreadyClaimedError,
  isEmailOnAllowedDomain,
  requiresVerifiedEmail,
} from 'loyalty/tierUpgradeCampaign/emailDomainEligibility'
import { isTierUpgradeCampaignPath } from 'loyalty/tierUpgradeCampaign/tierUpgradeCampaignRoutes'
import { useClaimedTierUpgradeCampaignIds } from 'loyalty/tierUpgradeCampaign/tierUpgradeClaimHistory'
import { getTierUpgradeClaimErrorDetails } from 'loyalty/pages/tierUpgradeCampaign/useTierUpgradeClaimErrorMessage'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { onEmailVerifiedInAnotherTab } from 'lib/auth/emailVerificationBroadcast'
import { onTierUpgradeClaimedInAnotherTab } from 'loyalty/tierUpgradeCampaign/tierUpgradeCampaignTabSync'

/**
 * Grants email-domain tier upgrade campaigns wherever the customer is on the site: as soon as
 * they're logged in with a verified email on a live campaign's allowed domains, they're enrolled in
 * Société if needed and the tier is claimed. Customers don't need to visit the landing page (e.g.
 * they sign up and verify from the homepage). The global rewards notification celebrates the new
 * tier once the loyalty account reloads.
 *
 * An email-domain campaign is a Direct (codeless) claim campaign with an email domain allowlist; Direct
 * campaigns open to every member are only granted on their own landing page, never site-wide.
 *
 * Only does work while a live campaign would upgrade the logged-in customer, and then loads the
 * active claim campaigns once per session. Steps aside from claiming on a campaign's own
 * landing page, which runs the claim itself with its own success modal, but still re-checks
 * verification there so the page can react as soon as the customer verifies.
 */
// While an eligible customer is unverified and looking at the tab, re-check verification this often...
const VERIFICATION_POLL_INTERVAL_MS = 10_000
// ...for at most this long after the page loaded, so a customer who never verifies doesn't poll forever
const VERIFICATION_POLL_MAX_MS = 15 * 60_000

function useEmailDomainTierUpgradeAutoGrant() {
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const campaigns = useTierUpgradeCampaigns()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  // Only meaningful while logged in: the logged-out account defaults to verified
  const emailVerified = useAppSelector(state => state.auth.account.emailVerified)
  const email = useAppSelector(state => state.auth.account.email)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const loyaltyAccount = useAppSelector(state => state.loyalty.account)
  const claimCampaigns = useAppSelector(state => state.loyalty.claimCampaigns)

  const enrolRequested = useRef(false)
  const enrolRecoveryRequested = useRef(false)
  // One attempt per campaign per page session, so a failing claim can't loop
  const claimsAttempted = useRef(new Set<string>())

  const loyaltyAccountReady = !loyaltyAccount.initial && !loyaltyAccount.fetching
  const currentTier = loyaltyAccount.data?.tier

  // Live campaigns that would upgrade this customer (non-members can join any). Whether one is an
  // email-domain campaign is only known once its claim campaign has loaded.
  const upgradableCampaigns = useMemo(() => {
    if (!isLoggedIn || !loyaltyAccountReady) return []
    return campaigns.filter(campaign => getCampaignTierStanding(currentTier, campaign.tiers) === 'upgradable')
  }, [campaigns, currentTier, isLoggedIn, loyaltyAccountReady])

  // ...that are email-domain campaigns this customer's email is eligible for
  const eligibleCampaigns = useMemo(() => upgradableCampaigns.filter(campaign => {
    const claimCampaign = claimCampaigns.data?.[campaign.campaignId]
    return !!claimCampaign &&
      canAutoClaimCampaign(claimCampaign) &&
      requiresVerifiedEmail(claimCampaign) &&
      isEmailOnAllowedDomain(email, claimCampaign.emailVerificationDomains)
  }), [upgradableCampaigns, claimCampaigns.data, email])
  const hasEligibleCampaign = eligibleCampaigns.length > 0

  // The one to claim here: a campaign's landing page claims for itself, and a campaign they've
  // already claimed (e.g. their complimentary tier has since ended) can't be claimed again
  const claimCandidates = eligibleCampaigns.filter(campaign => !isTierUpgradeCampaignPath(pathname, campaign.slug))
  const claimedCampaignIds = useClaimedTierUpgradeCampaignIds(claimCandidates.length > 0 && !!emailVerified)
  const eligibleCampaign = claimedCampaignIds && claimCandidates.find(campaign => !claimedCampaignIds.has(campaign.campaignId))
  const eligibleCampaignId = eligibleCampaign?.campaignId
  const eligibleCampaignTiers = useRef(eligibleCampaign?.tiers)
  eligibleCampaignTiers.current = eligibleCampaign?.tiers

  // The allowlist lives on the claim campaign, so it's only loaded for customers who could be upgraded
  useEffect(() => {
    if (upgradableCampaigns.length && !claimCampaigns.data && !claimCampaigns.error) {
      dispatch(fetchLoyaltyClaimCampaigns())
    }
  }, [upgradableCampaigns.length, claimCampaigns.data, claimCampaigns.error, dispatch])

  // Pick up a verification done elsewhere (another tab, browser or device), without the customer
  // having to refresh: another tab tells us straight away; otherwise we check when they come back to
  // this tab and keep checking every few seconds while it's visible. Browsers don't reliably fire focus
  // or visibility events for every way of switching back, so the polling is what guarantees it. Runs
  // on landing pages too, which react to the verified state with their own claim.
  const pollStartedAt = useRef(Date.now())
  useEffect(() => {
    if (!isLoggedIn || emailVerified) return
    const syncVerification = () => {
      me()
        .then(account => {
          if (account.emailVerified) dispatch(syncUserDetails({ emailVerified: true }))
        })
        .catch(() => {})
    }
    const unsubscribe = onEmailVerifiedInAnotherTab(syncVerification)
    if (!hasEligibleCampaign) return unsubscribe

    const syncIfVisible = () => {
      if (document.visibilityState === 'visible') syncVerification()
    }
    const pollTimer = setInterval(() => {
      if (Date.now() - pollStartedAt.current > VERIFICATION_POLL_MAX_MS) {
        clearInterval(pollTimer)
        return
      }
      syncIfVisible()
    }, VERIFICATION_POLL_INTERVAL_MS)
    window.addEventListener('focus', syncVerification)
    document.addEventListener('visibilitychange', syncIfVisible)
    return () => {
      unsubscribe()
      clearInterval(pollTimer)
      window.removeEventListener('focus', syncVerification)
      document.removeEventListener('visibilitychange', syncIfVisible)
    }
  }, [dispatch, emailVerified, hasEligibleCampaign, isLoggedIn])

  // Another tab claimed: reload the tier so this tab shows it (and celebrates it)
  useEffect(() => {
    if (!isLoggedIn) return
    return onTierUpgradeClaimedInAnotherTab((campaignId) => {
      const campaignTiers = campaigns.find(campaign => campaign.campaignId === campaignId)?.tiers ?? []
      dispatch(refreshLoyaltyAccountAfterTierClaim(campaignTiers))
    })
  }, [campaigns, dispatch, isLoggedIn])

  useEffect(() => {
    if (!eligibleCampaignId || !emailVerified || loyaltyAccount.fetching) return

    if (!hasLoyaltyAccount) {
      if (!enrolRequested.current) {
        if (!loyaltyAccount.error) {
          enrolRequested.current = true
          dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
        }
        return
      }
      // Enrolling failed, e.g. this tab's account was stale and they've since joined Société (and
      // claimed) in another browser: reload once to pick up their account, then claim as usual
      if (loyaltyAccount.error && !enrolRecoveryRequested.current) {
        enrolRecoveryRequested.current = true
        dispatch(fetchLoyaltyAccount(undefined, { force: true }))
      }
      return
    }

    if (claimsAttempted.current.has(eligibleCampaignId)) return
    claimsAttempted.current.add(eligibleCampaignId)

    const campaignTiers = eligibleCampaignTiers.current ?? []
    claimTierUpgradeCampaign(eligibleCampaignId, memberId)
      .then(() => dispatch(refreshLoyaltyAccountAfterTierClaim(campaignTiers)))
      .catch((err) => {
        const { errorCode } = getTierUpgradeClaimErrorDetails(err)
        // Already claimed, or the loaded tier was stale: reload so the page reflects the real tier
        if (isAlreadyClaimedError(errorCode) || errorCode === 'TIER_ALREADY_HIGHER') {
          dispatch(refreshLoyaltyAccountAfterTierClaim(campaignTiers))
        }
      })
  }, [dispatch, eligibleCampaignId, emailVerified, hasLoyaltyAccount, loyaltyAccount.error, loyaltyAccount.fetching, memberId])
}

export default useEmailDomainTierUpgradeAutoGrant
